/**
 * 個別説明可能枠 (briefing_schedules) の作成・編集ダイアログ。
 */
import type { BriefingScheduleStyle } from '@goenhub/shared';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useAdminDirectory } from '@/hooks/useAdmins';
import { useAuth } from '@/hooks/useAuth';
import {
  useUpsertBriefingSchedule,
  type BriefingScheduleRecord,
} from '@/hooks/useBriefings';

interface FormValues {
  date: string;
  startTime: string;
  endTime: string;
  style: BriefingScheduleStyle;
  zoomUrl: string;
  zoomId: string;
  location: string;
  capacity: number;
  isActive: boolean;
  /** 担当者 uid。管理者は選択可、SA は自分に固定。 */
  assigneeUid: string;
}

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const defaults: FormValues = {
  date: todayISO(),
  startTime: '14:00',
  endTime: '15:00',
  style: 'online',
  zoomUrl: '',
  zoomId: '',
  location: '',
  capacity: 1,
  isActive: true,
  assigneeUid: '',
};

interface Props {
  open: boolean;
  existing: BriefingScheduleRecord | null;
  onOpenChange: (open: boolean) => void;
}

export function BriefingScheduleDialog({ open, existing, onOpenChange }: Props) {
  const upsertMutation = useUpsertBriefingSchedule();
  const { user } = useAuth();
  // 管理者 (owner / 外部SA 以外) は担当者を任意に割り当て可。SA は自分に固定。
  const isManager = Boolean(user?.isOwner) || (!!user && user.level !== 'external');
  // 2026-08-18: admins コレクションの直読み (useAdmins) は firestore.rules で owner 限定のため、
  // 外部SA が枠ダイアログを開くたびに permission-denied を起こしていた。
  // 表示用の最小名簿 Callable (全ロール可) に切り替える。担当者セレクトは管理者のみ使用。
  const { data: admins } = useAdminDirectory({ enabled: isManager });
  const activeMembers = (admins ?? []).filter((a) => a.resolvedStatus === 'active');
  const form = useForm<FormValues>({ defaultValues: defaults });

  // フォーム初期化は「ダイアログを開いた瞬間」と「編集対象が変わった瞬間」だけに限定する。
  // 2026-08-18: 依存に form / user?.uid / existing(オブジェクト参照) を含めていたため、
  // 親の再レンダー (React Query の再取得など) で reset が再実行され、入力中の選択が
  // 初期値に巻き戻る事故が起き得た (参加スタイルが常にオンラインへ戻る等)。
  useEffect(() => {
    if (!open) return;
    if (existing) {
      form.reset({
        date: existing.date,
        startTime: existing.startTime,
        endTime: existing.endTime,
        style: existing.style,
        zoomUrl: existing.zoomUrl ?? '',
        zoomId: existing.zoomId ?? '',
        location: existing.location ?? '',
        capacity: existing.capacity,
        isActive: existing.isActive,
        assigneeUid: existing.assigneeUid ?? '',
      });
    } else {
      form.reset({ ...defaults, assigneeUid: user?.uid ?? '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id]);

  const style = form.watch('style');
  const isActive = form.watch('isActive');
  const assigneeUid = form.watch('assigneeUid');

  const onSubmit = async (values: FormValues) => {
    try {
      await upsertMutation.mutateAsync({
        id: existing?.id,
        date: values.date,
        startTime: values.startTime,
        endTime: values.endTime,
        style: values.style,
        zoomUrl: values.zoomUrl.trim() || null,
        zoomId: values.zoomId.trim() || null,
        location: values.location.trim() || null,
        capacity: Number(values.capacity) || 1,
        isActive: values.isActive,
        // 担当者は管理者のみ送信 (SA はサーバ側で自分に固定)。
        assigneeUid: isManager ? values.assigneeUid || null : undefined,
      });
      toast.success(existing ? '更新しました' : '枠を追加しました');
      onOpenChange(false);
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{existing ? '枠を編集' : '個別説明枠を追加'}</DialogTitle>
          {/* SA (管理者以外) は担当者が自分に固定される旨を補足表示。 */}
          {!isManager && user && (
            <div className="text-[11px] text-[var(--text-muted)] font-mono">
              担当者: {user.email ?? '(no email)'}（自分に固定）
            </div>
          )}
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          {/* 管理者のみ担当者を選択可。SA は非表示 (サーバ側で自分に固定)。 */}
          {isManager && (
            <div>
              <Label>担当者</Label>
              <Select
                value={assigneeUid ?? ''}
                onValueChange={(v) =>
                  form.setValue('assigneeUid', v, { shouldDirty: true })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="担当者を選択" />
                </SelectTrigger>
                <SelectContent>
                  {activeMembers.map((m) => (
                    <SelectItem key={m.uid} value={m.uid}>
                      {m.fullName || m.displayName || m.email || m.uid}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label htmlFor="bs-date">日付</Label>
            <Input id="bs-date" type="date" {...form.register('date', { required: true })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="bs-start">開始</Label>
              <Input id="bs-start" type="time" {...form.register('startTime', { required: true })} />
            </div>
            <div>
              <Label htmlFor="bs-end">終了</Label>
              <Input id="bs-end" type="time" {...form.register('endTime', { required: true })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>参加スタイル</Label>
              <Select
                value={style ?? 'online'}
                onValueChange={(v) =>
                  form.setValue('style', v as BriefingScheduleStyle, {
                    shouldDirty: true,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">オンライン</SelectItem>
                  <SelectItem value="onsite">現地</SelectItem>
                  <SelectItem value="both">両方可</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bs-cap">受入人数</Label>
              <Input
                id="bs-cap"
                type="number"
                min={1}
                {...form.register('capacity', { valueAsNumber: true })}
              />
            </div>
          </div>
          {(style === 'online' || style === 'both') && (
            <>
              <div>
                <Label htmlFor="bs-zurl">Zoom URL</Label>
                <Input id="bs-zurl" type="url" {...form.register('zoomUrl')} />
              </div>
              <div>
                <Label htmlFor="bs-zid">Zoom ID</Label>
                <Input id="bs-zid" {...form.register('zoomId')} />
              </div>
            </>
          )}
          {(style === 'onsite' || style === 'both') && (
            <div>
              <Label htmlFor="bs-loc">場所</Label>
              <Input id="bs-loc" placeholder="例: 東京オフィス 5F" {...form.register('location')} />
            </div>
          )}
          <div className="flex items-center justify-between rounded-sm border border-[var(--border)] px-3 py-2">
            <Label className="!mb-0">アクティブ</Label>
            <Switch
              checked={isActive}
              onChange={(e) => form.setValue('isActive', e.target.checked)}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={upsertMutation.isPending}
            >
              キャンセル
            </Button>
            <Button type="submit" variant="primary" disabled={upsertMutation.isPending}>
              {upsertMutation.isPending ? '保存中...' : existing ? '更新' : '追加'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
